import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, HelpTooltip } from '@fiado/react-kit';
import { Label } from '../../../../components/ui/label';
import type { LevelDefLocal } from '../../../../api/types/ApplicationLocal';

// El `level` (scope) de cada nivel se asigna por POSICIÓN (fila 1 → LEVEL_1, etc.). Máximo 3: LEVEL_1..3
// son los únicos scopes de nivel del catálogo (ver PermissionScope en backoffice.gen.ts). Extraído de
// CreateApplicationForm (T4) para que crear (Task D) y editar (Task E) compartan la misma lógica.
export const POSITION_TO_SCOPE = ['LEVEL_1', 'LEVEL_2', 'LEVEL_3'] as const;
export const MAX_LEVELS = POSITION_TO_SCOPE.length;

/** Borrador de un nivel: solo el nombre legible; el `level` se deriva por posición al persistir. */
export interface LevelDraft {
    displayName: string;
}

/**
 * Mapea el borrador controlado a los `LevelDefLocal` del contrato, asignando el scope por posición.
 * El padre (wizard de crear / detalle de editar) lo usa al armar el payload.
 */
export function levelsToPayload(value: LevelDraft[]): LevelDefLocal[] {
    return value.slice(0, MAX_LEVELS).map((lvl, i) => ({
        level: POSITION_TO_SCOPE[i],
        displayName: lvl.displayName.trim(),
    }));
}

interface Props {
    /** Lista controlada de niveles (0–3). El `level` se deriva por posición, no se guarda aquí. */
    value: LevelDraft[];
    /** Se llama con la lista completa actualizada en cada agregar/editar/quitar. */
    onChange: (next: LevelDraft[]) => void;
    /** Errores por posición (ej. del schema del padre). `errors[i]` es el mensaje del nivel i. */
    errors?: (string | undefined)[];
    /** Prefijo de id para a11y (htmlFor). Default `level`. */
    idPrefix?: string;
}

/**
 * Paso CONTROLADO y PURO de niveles dinámicos del armador de aplicaciones (#3-a). No tiene estado ni
 * llamadas API propias: recibe `value` + `onChange` y los reporta al padre. Muestra el label legible
 * "Nivel N: [nombre]" (nunca el scope crudo LEVEL_1/2/3, regla anti-id en UI).
 */
export function LevelsStep({ value, onChange, errors, idPrefix = 'level' }: Props) {
    const add = (): void => {
        onChange([...value, { displayName: '' }]);
    };
    const edit = (i: number, displayName: string): void => {
        onChange(value.map((lvl, idx) => (idx === i ? { displayName } : lvl)));
    };
    const remove = (i: number): void => {
        onChange(value.filter((_, idx) => idx !== i));
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                    <Label>Niveles</Label>
                    <HelpTooltip content="Niveles jerárquicos de la app (hasta 3). El alcance LEVEL_1/2/3 se asigna por la posición de la fila, no por el nombre." />
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" />}
                    disabled={value.length >= MAX_LEVELS}
                    onClick={add}
                >
                    Agregar nivel
                </Button>
            </div>

            {value.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                    Sin niveles: aplicación plana (válido). Agrega hasta {MAX_LEVELS}.
                </p>
            ) : (
                <ul className="space-y-2">
                    {value.map((lvl, i) => {
                        const n = String(i + 1);
                        // Label legible "Nivel N: nombre" — nunca el scope crudo (anti-id en UI).
                        const label = `Nivel ${n}: ${lvl.displayName}`.trimEnd();
                        return (
                            <li key={`${idPrefix}-${n}`} className="flex items-end gap-2">
                                <div className="flex-1">
                                    <Input
                                        label={label}
                                        placeholder={`Nombre del nivel ${n}`}
                                        value={lvl.displayName}
                                        error={errors?.[i]}
                                        onChange={(e) => {
                                            edit(i, e.target.value);
                                        }}
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Quitar nivel ${n}`}
                                    onClick={() => {
                                        remove(i);
                                    }}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
